import { inject, Injectable } from '@angular/core';
import {
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { filter } from 'rxjs/operators';
import { NewRelicService } from './newrelic.service';

@Injectable({ providedIn: 'root' })
export class NewRelicRouterTrackerService {
  private readonly _router = inject(Router);
  private readonly _newRelic = inject(NewRelicService);

  private _navigationStartTime = 0;
  private _currentUrl = '';
  private _previousUrl = '';

  startTracking(): void {
    if (this._newRelic.isReady()) {
      this._initializeTracking();
    } else {
      this._waitForNewRelic();
    }
  }

  private _waitForNewRelic(attempt = 1, maxAttempts = 10): void {
    const delay = Math.min(100 * Math.pow(2, attempt - 1), 3000);

    setTimeout(() => {
      if (this._newRelic.isReady()) {
        console.info('New Relic is ready, initializing router tracking');
        this._initializeTracking();
      } else if (attempt < maxAttempts) {
        console.warn(
          `New Relic not ready, retrying in ${delay}ms (attempt ${attempt}/${maxAttempts})`,
        );
        this._waitForNewRelic(attempt + 1, maxAttempts);
      } else {
        console.warn(
          `New Relic not ready after ${maxAttempts} attempts, router tracking disabled`,
        );
      }
    }, delay);
  }

  private _initializeTracking(): void {
    this._router.events
      .pipe(
        filter(
          (event: Event): event is NavigationStart =>
            event instanceof NavigationStart,
        ),
      )
      .subscribe((event: NavigationStart) => {
        this._navigationStartTime = performance.now();
        this._previousUrl = this._currentUrl;
        this._currentUrl = event.url;

        this._newRelic.addPageAction('RouteChangeStart', {
          url: event.url,
          previousUrl: this._previousUrl,
          navigationTrigger: event.navigationTrigger,
          restoredState: event.restoredState ? 'yes' : 'no',
        });
      });

    this._router.events
      .pipe(
        filter(
          (event: Event): event is NavigationEnd =>
            event instanceof NavigationEnd,
        ),
      )
      .subscribe((event: NavigationEnd) => {
        const duration = performance.now() - this._navigationStartTime;

        this._newRelic.addPageAction('RouteChangeComplete', {
          url: event.urlAfterRedirects,
          previousUrl: this._previousUrl,
          duration: Math.round(duration),
          status: 'success',
        });

        this._newRelic.setPageViewName(
          this._extractPageName(event.urlAfterRedirects),
        );
        this._newRelic.trackMetric('RouteChangeDuration', duration, 'ms');
      });

    this._router.events
      .pipe(
        filter(
          (event: Event): event is NavigationError =>
            event instanceof NavigationError,
        ),
      )
      .subscribe((event: NavigationError) => {
        const duration = performance.now() - this._navigationStartTime;

        this._newRelic.addPageAction('RouteChangeError', {
          url: event.url,
          previousUrl: this._previousUrl,
          duration: Math.round(duration),
          status: 'error',
          errorMessage: event.error?.message ?? 'Unknown navigation error',
        });

        this._newRelic.noticeError(
          new Error(
            `Navigation Error: ${event.error?.message ?? 'Unknown error'}`,
          ),
          {
            errorType: 'NavigationError',
            url: event.url,
            previousUrl: this._previousUrl,
          },
        );
      });

    this._router.events
      .pipe(
        filter(
          (event: Event): event is NavigationCancel =>
            event instanceof NavigationCancel,
        ),
      )
      .subscribe((event: NavigationCancel) => {
        const duration = performance.now() - this._navigationStartTime;

        this._newRelic.addPageAction('RouteChangeCancel', {
          url: event.url,
          previousUrl: this._previousUrl,
          duration: Math.round(duration),
          status: 'cancelled',
          reason: event.reason,
        });
      });
  }

  private _extractPageName(url: string): string {
    let cleanUrl = url.split('?')[0].split('#')[0];

    if (cleanUrl.startsWith('/')) {
      cleanUrl = cleanUrl.substring(1);
    }

    if (!cleanUrl) {
      return 'Home';
    }

    return cleanUrl
      .split('/')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join('.');
  }
}
